import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import ParticleField from '@/components/effects/ParticleField';
import GoldenSpiral from '@/components/effects/GoldenSpiral';
import ScrollReveal from '@/components/effects/ScrollReveal';
import TariqNameSection from '@/components/effects/TariqNameSection';
import FlipBook from '@/components/effects/FlipBook';
import VoidLayer from '@/components/effects/VoidLayer';
import { useGravityToast } from '@/components/effects/GravityToast';
import { ArrowRight, BookOpen, Users, Pen, Calendar, ChevronRight, Send } from 'lucide-react';

const TARIQ_PHOTO = '/assets/tariq-photo.jpg';

export default function Landing() {
  const { t, localizedField, lang } = useLang();
  const { user } = useAuth();
  const toast = useGravityToast();
  const [email, setEmail] = useState('');
  const [stats, setStats] = useState({ member_count: 0, thread_count: 0, post_count: 0 });
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: 'general', message: '' });
  const [settings, setSettings] = useState({});
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get('/public-stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/journal?limit=3').then(r => setPosts(r.data.posts || [])).catch(() => {});
    api.get('/products').then(r => setProducts(r.data || [])).catch(() => {});
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    api.get('/events').then(r => setEvents((r.data || []).filter(e => e.is_published !== false && e.status !== 'past'))).catch(() => {});
    api.get('/courses').then(r => setCourses(r.data || [])).catch(() => {});
  }, []);

  const book = products.find(p => p.slug === 'center-of-gravity');
  const digitalBook = products.find(p => p.slug === 'center-of-gravity-digital');

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await api.post('/newsletter', { email, email_lang: lang });
      toast.success(t('newsletter.subscribed'));
      setEmail('');
    } catch { toast.error('Failed to subscribe'); }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', contactForm);
      toast.success(t('contact.sent'));
      setContactForm({ name: '', email: '', subject: 'General', message: '' });
    } catch { toast.error('Failed to send'); }
  };

  const heroTitle = settings.hero_title?.[lang] || t('hero.title');
  const heroSubtitle = settings.hero_subtitle?.[lang] || t('hero.subtitle');
  const heroTagline = settings.hero_tagline?.[lang] || t('hero.tagline');

  return (
    <div data-testid="landing-page">
      {/* === HERO: THE GRAVITY WELL === */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-16 md:-mt-20" data-testid="hero-section">
        {/* SVG filter for nebula distortion */}
        <svg className="absolute w-0 h-0" aria-hidden="true">
          <defs>
            <filter id="void-warp" x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
              <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="4" seed="3" stitchTiles="stitch" result="noise" />
              <feGaussianBlur in="noise" stdDeviation="8" result="smoothNoise" />
              <feDisplacementMap in="SourceGraphic" in2="smoothNoise" scale="50" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
        </svg>

        {/* Breathing nebula void — SVG filter + CSS blur(2px) to kill last seam.
            VoidLayer renders the original div on Chromium/Firefox, and the
            identical gradient + turbulence filter as inline SVG on WebKit
            (Safari/iOS can't apply the filter to an HTML element). */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: 'void-breathe 8s ease-in-out infinite' }}>
          <VoidLayer />
        </div>

        {/* GoldenSpiral overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <GoldenSpiral size={600} className="animate-spiral-rotate" opacity={0.12} />
        </div>

        {/* Particles, sparks, shooting stars */}
        <ParticleField heroMode />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <ScrollReveal>
            <h1
              className="text-5xl sm:text-6xl lg:text-8xl font-display font-bold shimmer-text mb-4"
              style={{ animation: 'shimmer 4s linear infinite, float-gentle 6s ease-in-out infinite' }}
              data-testid="hero-title"
            >
              {heroTitle}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p
              className="text-lg sm:text-xl text-cream/70 font-body tracking-[0.08em] sm:tracking-[0.2em] uppercase mb-6"
              style={{ animation: 'float-gentle 6s ease-in-out infinite 0.5s' }}
              data-testid="hero-subtitle"
            >
              {heroSubtitle}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p
              className="text-base sm:text-lg text-cream/50 font-accent italic max-w-2xl mx-auto mb-10"
              style={{ animation: 'float-gentle 7s ease-in-out infinite 1s' }}
              data-testid="hero-tagline"
            >
              {heroTagline}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <a href="#book-section" className="hero-cta-button inline-flex items-center gap-2 px-8 py-3 rounded-full font-body text-base" data-testid="hero-cta">
              {t('hero.cta')} <ArrowRight size={18} />
            </a>
          </ScrollReveal>
        </div>
      </section>

      {/* === BOOK FEATURE === */}
      <section id="book-section" className="py-20 md:py-32 px-4" data-testid="book-section">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
              <div className="flex justify-center">
                <FlipBook className="w-64 md:w-80 aspect-[2/3]" />
              </div>
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display" data-testid="book-title">
                  {localizedField(book?.title) || t('book.title')}
                </h2>
                <p className="text-lg font-accent italic text-sacred-gold">{t('book.subtitle')}</p>
                <p className="text-warm-brown font-body leading-relaxed">{localizedField(book?.description)}</p>
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <p className="text-3xl font-display">${book?.price_usd || '22.99'} <span className="text-base text-text-muted">USD</span></p>
                    {book?.price_ils && <p className="text-sm text-text-muted">&#8362;{book.price_ils} ILS</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/book" className="gold-button px-6 py-3 rounded-full font-body text-sm" data-testid="buy-physical-btn">
                    <BookOpen size={16} className="inline me-2" />{t('book.buyNow')}
                  </Link>
                  <Link to="/book" className="glass-button px-6 py-3 rounded-full font-body text-sm" data-testid="buy-digital-btn">
                    {t('book.buyDigital')}{digitalBook?.price_usd ? ` — $${digitalBook.price_usd}` : ''}
                  </Link>
                </div>
                <Link to="/book#free-chapter" className="inline-flex items-center gap-2 text-sacred-gold font-body text-sm hover:text-molten-gold transition-colors" data-testid="free-chapter-link">
                  {t('readerMagnet.title')} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* === ABOUT PREVIEW === */}
      <section className="py-20 md:py-32 px-4 relative" data-testid="about-preview">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12 items-center">
          <ScrollReveal className="md:col-span-2">
            <div className="relative w-full max-w-sm mx-auto">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden gold-glow relative">
                <img src={TARIQ_PHOTO} alt="Tariq AbuFiad" loading="lazy" className="w-full h-full object-cover" data-testid="tariq-photo" />
                <span className="absolute bottom-2 right-3 text-white/50 text-[10px] font-body tracking-wide" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>Oct 4, 2025</span>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal className="md:col-span-3 space-y-6" delay={0.1}>
            <h2 className="text-3xl sm:text-4xl font-display">{t('about.title')}</h2>
            <p className="text-warm-brown font-body leading-relaxed">
              {lang === 'ar'
                ? '\u0642\u0636\u064a\u062a \u0645\u0639\u0638\u0645 \u062d\u064a\u0627\u062a\u064a \u0623\u062d\u0627\u0648\u0644 \u0623\u0646 \u0623\u0641\u0647\u0645 \u0634\u064a\u0626\u0627\u064b \u0648\u0627\u062d\u062f\u0627\u064b: \u0645\u0627 \u0645\u0639\u0646\u0649 \u0623\u0646 \u062a\u062d\u0636\u0631 \u0628\u0635\u062f\u0642 \u2014 \u0644\u0646\u0641\u0633\u0643\u060c \u0644\u0639\u0645\u0644\u0643\u060c \u0644\u0644\u0646\u0627\u0633 \u0645\u0646 \u062d\u0648\u0644\u0643\u060c \u0648\u0644\u0634\u064a\u0621 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0643\u0644 \u0630\u0644\u0643. \u0623\u0646\u0627 \u062e\u0627\u0644\u0642 \u0628\u0637\u0628\u0639\u064a. \u0644\u0643\u0646 \u0623\u0643\u062b\u0631 \u0645\u0646 \u0630\u0644\u0643\u060c \u0623\u0646\u0627 \u0645\u0624\u0645\u0646 \u2014 \u0628\u0627\u0644\u0644\u0647\u060c \u0628\u0627\u0644\u0646\u0627\u0633\u060c \u0628\u0627\u0644\u0645\u0633\u0627\u0631\u060c \u0648\u0628\u0641\u0643\u0631\u0629 \u0623\u0646 \u0644\u0627 \u0634\u064a\u0621 \u062a\u0645\u0631 \u0628\u0647 \u064a\u0636\u064a\u0639.'
                : 'I\u2019ve spent most of my life trying to understand one thing: what it means to truly show up \u2014 for yourself, for your work, for the people around you, and for something bigger than all of it. I am a creator by nature. But more than that, I am a believer \u2014 in God, in people, in the process, and in the idea that nothing you go through is wasted.'}
            </p>
            <div className="pull-quote">
              "{lang === 'ar'
                ? '\u0628\u0646\u064a\u062a \u0647\u0630\u0627 \u0627\u0644\u0639\u0645\u0644 \u0644\u064a\u0633 \u0644\u0623\u0628\u062b \u0646\u0641\u0633\u064a\u060c \u0628\u0644 \u0644\u0623\u0639\u0643\u0633 \u0634\u064a\u0626\u0627\u064b \u0644\u0644\u0646\u0627\u0633. \u062a\u0630\u0643\u064a\u0631 \u0628\u0623\u0646 \u0627\u0644\u062c\u0627\u0630\u0628\u064a\u0629 \u062a\u064f\u0628\u0646\u0649 \u0645\u0646 \u0627\u0644\u062f\u0627\u062e\u0644.'
                : 'I built this work not to broadcast myself, but to reflect something back to people. A reminder that presence is built from the inside out.'}"
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 text-sacred-gold font-body hover:text-molten-gold transition-colors" data-testid="read-full-story">
              {t('about.readFull')} <ArrowRight size={16} />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* === TARIQ NAME MEANING === */}
      <TariqNameSection />

      {/* === COURSES PREVIEW === */}
      <section className="py-20 px-4" data-testid="courses-preview">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-3xl sm:text-4xl font-display">{t('courses.title')}</h2>
              {courses.length > 0 ? (
                <Link to="/courses" className="text-sacred-gold font-body text-sm flex items-center gap-1 hover:text-molten-gold">
                  {t('common.viewAll')} <ChevronRight size={16} />
                </Link>
              ) : (
                <span className="text-sacred-gold/50 text-sm font-body">{t('courses.comingSoon')}</span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.length > 0 ? courses.slice(0, 3).map((course, i) => (
                <ScrollReveal key={course.id} delay={i * 0.1}>
                  <Link to={`/courses/${course.slug}`} className="glass-card rounded-2xl overflow-hidden block group h-full">
                    {course.thumbnail_url ? (
                      <div className="aspect-video overflow-hidden">
                        <img src={course.thumbnail_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${course.thumbnail_url}` : course.thumbnail_url} alt={localizedField(course.title) || ''} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gold-deep/5 flex items-center justify-center">
                        <GoldenSpiral size={60} opacity={0.2} />
                      </div>
                    )}
                    <div className="p-5 space-y-2">
                      <h3 className="text-lg font-display group-hover:text-sacred-gold transition-colors">{localizedField(course.title)}</h3>
                      <p className="text-sacred-gold font-body text-sm">
                        {course.is_free ? t('events.free') : (course.price_usd ? `$${course.price_usd}` : '')}
                      </p>
                    </div>
                  </Link>
                </ScrollReveal>
              )) : [1, 2, 3].map(i => (
                <div key={i} className="glass-card rounded-2xl p-6 space-y-4">
                  <div className="w-full aspect-video bg-gold-deep/5 rounded-lg flex items-center justify-center">
                    <GoldenSpiral size={60} opacity={0.2} />
                  </div>
                  <div className="h-4 bg-gold-deep/5 rounded w-3/4" />
                  <div className="h-3 bg-gold-deep/5 rounded w-1/2" />
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* === EVENTS PREVIEW === */}
      <section className="py-20 px-4" data-testid="events-preview">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-display mb-10">{t('events.title')}</h2>
            {events.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {events.slice(0, 2).map((ev, i) => (
                  <ScrollReveal key={ev.id} delay={i * 0.1}>
                    <Link to={`/events/${ev.slug}`} className="glass-card rounded-2xl p-6 block group h-full">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-sacred-gold/10 flex items-center justify-center shrink-0">
                          <Calendar size={22} className="text-sacred-gold" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-display group-hover:text-sacred-gold transition-colors">{localizedField(ev.title)}</h3>
                          <p className="text-text-muted text-sm font-body mt-1">
                            {ev.event_date ? new Date(ev.event_date).toLocaleDateString(lang === 'ar' ? 'ar' : undefined, { dateStyle: 'medium' }) : ''}
                          </p>
                          <p className="text-sacred-gold font-body text-sm mt-2">
                            {ev.is_free ? t('events.free') : (ev.price_usd ? `$${ev.price_usd}` : '')}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
                <Calendar size={48} className="text-sacred-gold/40 mx-auto mb-4" />
                <p className="text-warm-brown font-body">{t('events.noEvents')}</p>
                <p className="text-text-muted text-sm font-body mt-2">{t('courses.comingSoon')}</p>
              </div>
            )}
          </ScrollReveal>
        </div>
      </section>

      {/* === JOURNAL PREVIEW === */}
      {posts.length > 0 && (
        <section className="py-20 px-4" data-testid="journal-preview">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal>
              <div className="flex items-center justify-between mb-10">
                <h2 className="text-3xl sm:text-4xl font-display">{t('journal.title')}</h2>
                <Link to="/journal" className="text-sacred-gold font-body text-sm flex items-center gap-1 hover:text-molten-gold">
                  {t('common.viewAll')} <ChevronRight size={16} />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post, i) => (
                  <ScrollReveal key={post.id} delay={i * 0.1}>
                    <Link to={`/journal/${post.slug}`} className="glass-card rounded-2xl overflow-hidden block group">
                      {post.header_image_url ? (
                        <div className="aspect-video overflow-hidden">
                          <img src={post.header_image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${post.header_image_url}` : post.header_image_url} alt={localizedField(post.title) || ''} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gold-deep/5 flex items-center justify-center">
                          <Pen size={32} className="text-sacred-gold/20" />
                        </div>
                      )}
                      <div className="p-5 space-y-2">
                        <h3 className="text-lg font-display group-hover:text-sacred-gold transition-colors">{localizedField(post.title)}</h3>
                        <p className="text-text-muted text-sm font-body">{post.published_at ? new Date(post.published_at).toLocaleDateString() : ''}</p>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* === COMMUNITY === */}
      <section className="py-20 px-4" data-testid="community-preview">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-display mb-4">{t('community.title')}</h2>
            <p className="text-warm-brown font-body mb-6 max-w-lg mx-auto">{t('community.subtitle')}</p>
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-3xl font-display text-sacred-gold">{stats.member_count}</p>
                <p className="text-text-muted text-sm font-body">{t('community.members')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-display text-sacred-gold">{stats.thread_count}</p>
                <p className="text-text-muted text-sm font-body">{t('forum.threads')}</p>
              </div>
            </div>
            <Link to="/forum" className="glass-button px-8 py-3 rounded-full font-body inline-flex items-center gap-2" data-testid="enter-forum-btn">
              <Users size={18} /> {t('community.enter')}
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* === NEWSLETTER === */}
      <section className="py-20 px-4" data-testid="newsletter-section">
        <div className="max-w-xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-display mb-3">{t('newsletter.title')}</h2>
            <p className="text-warm-brown font-body mb-8">{t('newsletter.subtitle')}</p>
            <form onSubmit={handleNewsletter} className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('newsletter.placeholder')}
                required
                data-testid="newsletter-email"
                className="flex-1 px-5 py-3 rounded-full glass-input font-body text-sm"
              />
              <button type="submit" data-testid="newsletter-submit" className="gold-button px-6 py-3 rounded-full font-body text-sm flex items-center gap-2">
                <Send size={16} /> {t('newsletter.subscribe')}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </section>

      {/* === CONTACT === */}
      <section className="py-20 px-4" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-display mb-4">{t('contact.title')}</h2>
                <p className="text-warm-brown font-body mb-6">{t('contact.subtitle')}</p>
                <p className="text-sacred-gold font-body">contact@tariqwithin.com</p>
              </div>
              <form onSubmit={handleContact} className="space-y-4">
                <input type="text" placeholder={t('contact.name')} required value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="contact-name" />
                <input type="email" placeholder={t('contact.email')} required value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="contact-email" />
                <select value={contactForm.subject} onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm bg-white" data-testid="contact-subject">
                  {Object.entries(t('contact.subjects')).map(([k, v]) => <option key={k} value={k} className="bg-white">{v}</option>)}
                </select>
                <textarea placeholder={t('contact.message')} required rows={4} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none" data-testid="contact-message" />
                <button type="submit" data-testid="contact-submit" className="gold-button px-6 py-3 rounded-full font-body text-sm">{t('contact.send')}</button>
              </form>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
